import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Home, Users, Heart, MapPin, Network, User, LogOut, Menu, X, Shield, Calendar, Mail } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';


export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const roleLabel = (r) => ({
    admin: 'Administrateur', contributor: 'Contributeur', reader: 'Lecture seule'
  }[r] || r);

  const navItems = [
    { path: '/dashboard', label: 'Tableau de bord', icon: Home,     testId: 'nav-link-dashboard' },
    { path: '/persons',   label: 'Personnes',        icon: Users,    testId: 'nav-link-persons' },
    { path: '/families',  label: 'Familles',         icon: Heart,    testId: 'nav-link-families' },
    { path: '/events',    label: 'Événements',       icon: Calendar, testId: 'nav-link-events' },
    { path: '/places',    label: 'Lieux',            icon: MapPin,   testId: 'nav-link-places' },
    { path: '/tree',      label: 'Arbre',            icon: Network,  testId: 'nav-link-tree' },
    { path: '/profile',   label: 'Mon profil',       icon: User,     testId: 'nav-link-profile' },
    ...(user?.role === 'admin'
      ? [{  path: '/admin/users',     label: 'Utilisateurs', icon: Shield,      testId: 'nav-link-admin' },
        {   path: '/admin/contact',   label: 'Demandes',     icon: Mail,        testId: 'nav-link-contact' },
      ]
      : []),
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--bg-sidebar)' }}>

      <div className="p-6 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--border)' }}>
        <div>
          <h1 className="text-2xl font-bold"
            style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
            Généalogie
          </h1>
          {user && (
            <p className="text-sm mt-1 truncate max-w-[150px]"
              style={{ color: 'var(--text-secondary)' }}>
              {user.prenom} {user.nom}
            </p>
          )}
          {user?.role && (
            <span className="text-xs px-2 py-0.5 mt-1 inline-block rounded-sm"
              style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
              {roleLabel(user.role)}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <ThemeToggle size="sm" />
          <button className="md:hidden" onClick={() => setSidebarOpen(false)}
            style={{ color: 'var(--text-muted)' }} aria-label="Fermer">
            <X size={20} />
          </button>
        </div>
      </div>

      <nav className="p-4 flex-1 overflow-y-auto">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            return (
              <li key={item.path}>
                <button
                  data-testid={item.testId}
                  onClick={() => navigate(item.path)}
                  className="w-full flex items-center space-x-3 px-4 py-3 transition-colors rounded-sm"
                  style={{
                    backgroundColor: isActive ? 'var(--nav-active-bg)' : 'transparent',
                    color: isActive ? 'var(--nav-active-text)' : 'var(--text-secondary)',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-4 py-3 transition-colors rounded-sm"
          style={{ color: 'var(--terracotta)' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <LogOut size={20} />
          <span className="font-medium">Déconnexion</span>
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="app-layout" className="flex min-h-screen"
      style={{ backgroundColor: 'var(--bg-page)' }}>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)} />
      )}

      <aside className="hidden md:flex md:flex-col w-64 fixed h-full z-30 border-r"
        style={{ borderColor: 'var(--border)' }}>
        <SidebarContent />
      </aside>

      <aside className={`fixed inset-y-0 left-0 z-30 w-64 flex flex-col border-r transform transition-transform duration-200 md:hidden ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ borderColor: 'var(--border)' }}>
        <SidebarContent />
      </aside>

      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        <header className="md:hidden sticky top-0 z-10 border-b px-4 py-3 flex items-center justify-between"
          style={{ backgroundColor: 'var(--bg-sidebar)', borderColor: 'var(--border)' }}>
          <button onClick={() => setSidebarOpen(true)} style={{ color: 'var(--forest)' }}
            aria-label="Menu">
            <Menu size={24} />
          </button>
          <span className="text-lg font-bold"
            style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
            Généalogie
          </span>
          <div className="flex items-center gap-2">
            <ThemeToggle size="sm" />
            <Link to="/profile" style={{ color: 'var(--text-muted)' }}><User size={22} /></Link>
          </div>
        </header>

        <main className="flex-1 p-6 md:p-10">
          {children}
        </main>
      </div>
    </div>
  );
};